/**
 * Bulk "Add several" on the Workforce page — the pure half.
 *
 * Coordinators onboard a shift at a time, and the one-at-a-time drawer made
 * that N round trips through the same three writes (minetwin `add_user`,
 * org `invite_project_user_by_email`, WorkOS invite). This module turns a
 * pasted list into validated rows and drives the SAME per-user pipeline
 * over them, one row at a time. Nothing here touches the network; the
 * component injects `addOne`, `sleep` and the catalog.
 *
 * Contract (mirrors the Cohorts chips rule, Samuel 2026-09-21): every line
 * the user pasted shows up in the preview with a reason when it will not
 * be added — never silently dropped — and every attempted row ends with an
 * outcome.
 *
 * No module-side bulk reducer: `add_user` is idempotent per email and a
 * per-row loop is what lets one bad address fail alone. The rows run
 * SEQUENTIALLY with a pause because WorkOS rate-limits invites per org
 * (WORKOS-INVITE-FLOW-PLAN § risks).
 */

/** One address pulled out of the pasted text, before validation. */
export type ParsedEntry = {
  /** Lowercased, trimmed; '' when the line held no address. */
  email: string;
  /** Text after the address on the same line, or null → use the default. */
  occupation: string | null;
  raw: string;
  /** 1-based line in the textarea, for the preview. */
  line: number;
};

export type RowProblem =
  | 'invalid-email'
  | 'duplicate'
  | 'already-on-site'
  | 'unknown-occupation'
  | 'missing-occupation';

export type BulkRow = {
  key: string;
  line: number;
  raw: string;
  email: string;
  /** Resolved occupation (catalog spelling when the catalog is loaded). */
  occupation: string;
  problem: RowProblem | null;
  /** Human reason for the problem, '' when ready. */
  note: string;
};

/** What happened to one row after the pipeline ran. */
export type RowOutcome = {
  kind:
    /** Workforce row written and a WorkOS invite email went out. */
    | 'invited'
    /** Workforce row written; the person already had an account / membership, so no email. */
    | 'added'
    /** Workforce row written but the WorkOS invite did not send — Resend later. */
    | 'invite-failed'
    /** Nothing written. */
    | 'failed';
  note: string;
};

/** Pause between rows — well under WorkOS's per-org invite limit. */
export const BULK_ROW_PAUSE_MS = 400;

const EMAIL_RE = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
/** An address anywhere in a token, optionally wrapped in <> (Outlook paste). */
const EMAIL_IN_TOKEN_RE = /<?([^\s@<>,;]+@[^\s@<>,;]+)>?/;

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email);
}

/**
 * Split pasted text into entries.
 *
 * Rules, deliberately simple enough to state in the placeholder:
 *   - one line per person; blank lines skipped;
 *   - tokens are separated by tab, comma or semicolon;
 *   - a token containing "@" is an address ("Jane <jane@x>" → jane@x);
 *   - text AFTER an address on the same line is that person's occupation;
 *     text BEFORE the first address (a display name) is ignored;
 *   - a line with no address is kept with email '' so validation can flag it.
 */
export function parseBulkInput(text: string): ParsedEntry[] {
  const out: ParsedEntry[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((rawLine, i) => {
    const raw = rawLine.trim();
    if (raw === '') return;
    const line = i + 1;
    const tokens = raw.split(/[\t,;]/).map((t) => t.trim()).filter((t) => t !== '');
    let sawEmail = false;
    for (const token of tokens) {
      const m = token.includes('@') ? EMAIL_IN_TOKEN_RE.exec(token) : null;
      if (m) {
        out.push({ email: m[1].toLowerCase(), occupation: null, raw, line });
        sawEmail = true;
      } else if (sawEmail) {
        // Occupation for the most recent address on this line.
        const last = out[out.length - 1];
        last.occupation = last.occupation ? `${last.occupation}, ${token}` : token;
      }
      // else: leading name/label — ignored.
    }
    if (!sawEmail) out.push({ email: '', occupation: null, raw, line });
  });
  return out;
}

export type BuildContext = {
  /** Applied to every entry without its own occupation; '' = none. */
  defaultOccupation: string;
  /** Catalog titles; null/empty = not loaded → accept as typed. */
  catalog: readonly string[] | null;
  /** Lowercased emails already active on the current site. */
  onSiteEmails: ReadonlySet<string>;
};

/** Validate entries into preview rows. Order preserved; keys stable per line+email. */
export function buildBulkRows(entries: readonly ParsedEntry[], ctx: BuildContext): BulkRow[] {
  const byLower = new Map<string, string>();
  for (const title of ctx.catalog ?? []) byLower.set(title.trim().toLowerCase(), title);
  const catalogLoaded = byLower.size > 0;
  const seen = new Set<string>();
  const keyCounts = new Map<string, number>();

  return entries.map((e) => {
    const baseKey = `${e.line}:${e.email}`;
    const n = (keyCounts.get(baseKey) ?? 0) + 1;
    keyCounts.set(baseKey, n);
    const key = n === 1 ? baseKey : `${baseKey}#${n}`;

    const typed = (e.occupation ?? ctx.defaultOccupation).trim();
    let occupation = typed;
    let problem: RowProblem | null = null;
    let note = '';

    if (!isValidEmail(e.email)) {
      problem = 'invalid-email';
      note = e.email === '' ? 'No email address on this line' : `"${e.email}" is not a valid address`;
    } else if (seen.has(e.email)) {
      problem = 'duplicate';
      note = 'Listed above already';
    } else if (ctx.onSiteEmails.has(e.email)) {
      problem = 'already-on-site';
      note = 'Already on this site — nothing to add';
    } else if (typed === '') {
      problem = 'missing-occupation';
      note = 'No occupation: pick a default or add one after the address';
    } else if (catalogLoaded) {
      const canonical = byLower.get(typed.toLowerCase());
      if (canonical) occupation = canonical;
      else {
        problem = 'unknown-occupation';
        note = `"${typed}" is not in the occupation list`;
      }
    }
    if (isValidEmail(e.email)) seen.add(e.email);

    return { key, line: e.line, raw: e.raw, email: e.email, occupation, problem, note };
  });
}

export type RunOptions = {
  pauseMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onProgress?: (row: BulkRow, outcome: RowOutcome) => void;
  /** Polled before each row; true stops the loop (Close mid-run). */
  isCancelled?: () => boolean;
};

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Run `addOne` over the rows one at a time with a pause between them.
 * A throw becomes a `failed` outcome so one bad row never aborts the rest.
 * Returns outcomes keyed by row key (only rows that were attempted).
 */
export async function runBulkAdd(
  rows: readonly BulkRow[],
  addOne: (row: BulkRow) => Promise<RowOutcome>,
  opts: RunOptions = {},
): Promise<Map<string, RowOutcome>> {
  const pauseMs = opts.pauseMs ?? BULK_ROW_PAUSE_MS;
  const sleep = opts.sleep ?? realSleep;
  const outcomes = new Map<string, RowOutcome>();
  for (let i = 0; i < rows.length; i++) {
    if (opts.isCancelled?.()) break;
    if (i > 0 && pauseMs > 0) await sleep(pauseMs);
    const row = rows[i];
    let outcome: RowOutcome;
    try {
      outcome = await addOne(row);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      outcome = { kind: 'failed', note: message.slice(0, 200) };
    }
    outcomes.set(row.key, outcome);
    opts.onProgress?.(row, outcome);
  }
  return outcomes;
}

/** The end-of-run toast. */
export function summarizeOutcomes(
  outcomes: readonly RowOutcome[],
): { title: string; sub: string; kind: 'good' | 'warn' | 'info' } {
  if (outcomes.length === 0) {
    return { title: 'No users added', sub: 'Nothing was attempted.', kind: 'info' };
  }
  const count = (k: RowOutcome['kind']) => outcomes.filter((o) => o.kind === k).length;
  const invited = count('invited');
  const added = count('added');
  const inviteFailed = count('invite-failed');
  const failed = count('failed');
  const written = invited + added + inviteFailed;
  const total = outcomes.length;
  const parts: string[] = [];
  if (invited > 0) parts.push(`${invited} invited by email`);
  if (added > 0) parts.push(`${added} already had an account and joined directly`);
  if (inviteFailed > 0) {
    parts.push(`${inviteFailed} added but the invite did not send (use Resend invite)`);
  }
  if (failed > 0) parts.push(`${failed} failed`);
  const ok = failed === 0 && inviteFailed === 0;
  return {
    title: ok
      ? `${total} user${total === 1 ? '' : 's'} added`
      : `${written} of ${total} user${total === 1 ? '' : 's'} added`,
    sub: parts.join(' · '),
    kind: ok ? 'good' : 'warn',
  };
}
